import { PriceTag } from "@/components/PriceTag";
import { imgPos } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listProducts } from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { TESTIDS } from "@/constants/testIds";

/**
 * SearchOverlay — fullscreen client-side product search.
 * Loads the catalogue once on open, filters by fuzzy match on name/sku/category.
 */
const SearchOverlay = ({ open, onClose }) => {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    if (open && catalog.length === 0) {
      listProducts().then(setCatalog).catch(() => setCatalog([]));
    }
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setQ("");
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, catalog.length]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return catalog.filter((p) => {
      const hay = `${p.name} ${p.sku} ${p.category} ${p.short_description || ""}`.toLowerCase();
      return needle.split(/\s+/).every((tok) => hay.includes(tok));
    }).slice(0, 12);
  }, [q, catalog]);

  if (!open) return null;

  return (
    <div
      data-testid={TESTIDS.searchOverlay}
      className="fixed inset-0 z-[70] bg-[#FAF7F1] fade-in overflow-y-auto"
    >
      <div className="max-w-3xl mx-auto pt-8 md:pt-14 px-6 md:px-8">
        <div className="flex items-center gap-3 border-b border-[#E3DFD7] pb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-[#6B6862] shrink-0"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
          <input
            data-testid={TESTIDS.searchInput}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent outline-none font-serif-display text-2xl md:text-4xl placeholder:text-[#B5999E] text-[#2C2C2C]"
          />
          <button
            data-testid={TESTIDS.searchClose}
            onClick={onClose}
            className="text-xs font-typewriter tracking-[0.22em] uppercase text-[#6B6862] hover:text-[#2C2C2C]"
            aria-label={t("search.close")}
          >
            ✕
          </button>
        </div>

        {q.trim() === "" ? (
          <p className="mt-6 text-sm text-[#6B6862]">{t("search.hint")}</p>
        ) : results.length === 0 ? (
          <p className="mt-6 text-sm text-[#6B6862]">{t("search.empty")}</p>
        ) : (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-6 pb-16">
            {results.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                data-testid={TESTIDS.searchResult(p.slug)}
                onClick={onClose}
                className="group"
              >
                <div className="rounded-card aspect-[3/4] overflow-hidden">
                  <img src={p.images?.[0]} alt={p.name} style={{ objectPosition: imgPos(p, p.images?.[0]) }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-2">
                  <div className="font-serif-display text-sm md:text-base text-[#2C2C2C] leading-tight truncate">{p.name}</div>
                  <PriceTag product={p} showPercent={false} className="font-typewriter text-[10px] text-[#2C2C2C]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;
